import axiosNest from '../api/axiosNest';
import type { CompanyModuleState, CompanyWithModules } from './modules';

/**
 * Moduły spółek z API (T.01, 06.10.2026).
 *
 * Odczyt: `GET /companies` — każdy zalogowany, pole `modules` przy każdej
 * spółce. Spółek są dwie, więc jedno zapytanie na sesję wystarczy; wynik
 * trzymamy w pamięci i odświeżamy po zmianie na ekranie Spółki, żeby menu
 * i karty od razu ukryły albo pokazały sekcję.
 *
 * Zapis: `PUT /companies/:id/modules/:module` z powodem — tylko
 * administrator instancji (zarządzanie spółkami).
 */

let cache: Promise<CompanyWithModules[] | null> | null = null;
const listeners = new Set<(companies: CompanyWithModules[] | null) => void>();

/**
 * Spółki z modułami. Błąd odczytu = `null` (nie wiemy) i bez zapamiętania —
 * kolejne wejście spyta jeszcze raz. Sekcje zostają wtedy widoczne:
 * o dostępie rozstrzyga serwer.
 */
export function loadCompanyModules(): Promise<CompanyWithModules[] | null> {
  if (!cache) {
    // Wywołanie w `then`, nie wprost: wyjątek rzucony przez klienta HTTP
    // (a nie odrzucona obietnica) też ma skończyć się „nie wiemy”.
    cache = Promise.resolve()
      .then(() => axiosNest.get('/companies'))
      .then((res) =>
        Array.isArray(res?.data) ? (res.data as CompanyWithModules[]) : [],
      )
      .catch(() => {
        cache = null;
        return null;
      });
  }
  return cache;
}

/** Powiadomienie o zmianie modułów — hak `useCompanyModules` przelicza widok. */
export function subscribeCompanyModules(
  listener: (companies: CompanyWithModules[] | null) => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Odczyt od nowa i powiadomienie wszystkich, którzy pokazują sekcje modułów. */
export async function reloadCompanyModules(): Promise<void> {
  cache = null;
  const companies = await loadCompanyModules();
  listeners.forEach((l) => l(companies));
}

/** Tylko dla testów: zapomnij zapamiętany odczyt. */
export function resetCompanyModulesCache(): void {
  cache = null;
  listeners.clear();
}

/** Włączenie albo wyłączenie modułu spółce, z powodem. Odpowiedź: moduły spółki. */
export async function saveCompanyModule(
  companyId: number | string,
  module: string,
  enabled: boolean,
  reason: string,
): Promise<CompanyModuleState[]> {
  const res = await axiosNest.put(
    `/companies/${companyId}/modules/${encodeURIComponent(module)}`,
    { enabled, reason },
  );
  return Array.isArray(res.data) ? (res.data as CompanyModuleState[]) : [];
}
